import Select from "react-select";
import { Controller } from "react-hook-form";
import { FormGroup, Label, Input, FormFeedback } from "reactstrap";
import DatePicker from "react-datepicker";
import { getErrorMessage } from "../../utils/form.utils";
import { arrayMap, checkTextInArray } from "../../utils/array.utils";

const FormField = ({
  label,
  id,
  type,
  options,
  placeholder,
  defaultValue,
  name,
  control,
  rules,
  errors,
  watch,
  customComponent,
  minDate,
  isClearable = true,
  isDisabled = false,
  setValue,
  unregister,
  setError,
  clearErrors,
  isMulti = false,
  others,
  helpText
}) => {
  const errorExists = errors && getErrorMessage(errors, name);
  const check = checkTextInArray(["checkbox"], type);

  const renderComponent = (field) => {
    switch (type) {
      case "select":
        return (
          <Select
            {...field}
            isClearable={isClearable}
            options={options}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isMulti={isMulti}
          />
        );
      case "date":
        return (
          <DatePicker
            {...field}
            isClearable
            id={id}
            placeholderText={placeholder}
            selected={field.value || null}
            onChange={(date) => field.onChange(date)}
            className="form-control"
            dateFormat="MMMM d, yyyy"
            minDate={minDate}
            disabled={isDisabled}
          />
        );
      case "datetime":
        return (
          <DatePicker
            {...field}
            isClearable
            id={id}
            placeholderText={placeholder}
            selected={field.value instanceof Date ? field.value : null}
            onChange={(date) => field.onChange(date instanceof Date ? date : null)}
            className="form-control"
            dateFormat="MMMM d, yyyy h:mm aa"
            minDate={minDate}
            disabled={isDisabled}
            showTimeSelect
            timeFormat="h:mm aa"
          />
        );
      case "radio":
        return (
          <div className="d-flex gap-5 flex-wrap">
            {arrayMap(options, (option) => (
              <FormGroup check key={option.value}>
                <Label check>
                  <Input
                    type="radio"
                    id={`${id}_${option.value}`}
                    name={name}
                    value={option.value}
                    checked={field.value === option.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                  {option.label}
                </Label>
              </FormGroup>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <div className="d-flex gap-5 flex-wrap">
            {arrayMap(options, (option) => (
              <FormGroup check key={option.value}>
                <Label check>
                  <Input
                    id={`${id}_${option.value}`}
                    type="checkbox"
                    value={option.value}
                    checked={field.value.includes(option.value)}
                    onChange={(e) => {
                      const newValue = [...field.value];
                      if (e.target.checked) {
                        newValue.push(e.target.value);
                      } else {
                        const index = newValue.indexOf(e.target.value);
                        if (index > -1) {
                          newValue.splice(index, 1);
                        }
                      }
                      field.onChange(newValue);
                    }}
                  />
                  {option.label}
                </Label>
              </FormGroup>
            ))}
          </div>
        );
      default:
        return (
          <>
            <Input
              {...field}
              id={id}
              type={type}
              placeholder={placeholder}
              invalid={!!errorExists}
              {...(check ? { checked: field?.value } : {})}
              disabled={isDisabled}
            />
            {!!helpText && <p className="m-0 pt-2">{helpText}</p>}
          </>
        );
    }
  };

  if (type === "custom") {
    const CustomComponent = customComponent;
    return (
      <CustomComponent
        setValue={setValue}
        setError={setError}
        clearErrors={clearErrors}
        control={control}
        errors={errors}
        watch={watch}
        others={others}
        unregister={unregister}
      />
    );
  }

  return (
    <FormGroup
      className={`position-relative ${!check ? `d-flex flex-column` : ``} ps-0`}
      check={check}>
      <Label htmlFor={id} check={check}>
        {label}
      </Label>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue || ""}
        rules={rules}
        render={({ field }) => (
          <>
            {renderComponent(field)}
            {errorExists && <FormFeedback className="d-block">{errorExists.message}</FormFeedback>}
          </>
        )}
      />
    </FormGroup>
  );
};

export default FormField;
